/**
 * Character density mapping for the Bestiary's ASCII engine.
 *
 * Period-appropriate character set, 12 density steps from sparsest
 * (whitespace) to densest (#). Maps a 0-255 luminance value to a
 * single character.
 *
 * The set is small enough to keep all 10 paintings reading as a
 * coherent typographic register, and the steps are coarse enough that
 * animation transitions (a `+` flickering to a `*`) read clearly as
 * motion rather than dissolving into texture.
 */

// Sparsest → densest. Index = density level (0..11).
export const CHARSET = [
  " ",
  "·",
  ".",
  ":",
  ";",
  "+",
  "*",
  "=",
  "%",
  "&",
  "@",
  "#",
];

export const DENSITY_LEVELS = CHARSET.length; // 12

/**
 * Characters used for the ornamental painting frame. AsciiCanvas
 * treats cells containing any of these as protected — animation
 * effects cannot overwrite them.
 *
 * Three layers nest inside the frame:
 *   • Outer rule (`╔═╗║╚╝`) — heavy double-line, most weight
 *   • Decorative band (`░`) — light-shade matte
 *   • Inner rule (`┌─┐│└┘`) — gentle single-line hold
 */
export const FRAME_CHARS = new Set([
  "╔", "╗", "╚", "╝", "═", "║",
  "┌", "┐", "└", "┘", "─", "│",
  "░", "▒", "▓",
]);

/**
 * Bosch palettes, density-mapped. Each character in CHARSET gets a
 * tone in each palette — sparse chars are pale, dense chars are deep
 * and saturated. The page color is the parchment `--color-page`
 * (#f1ebde), so all "pale" tones sit just barely darker than that.
 *
 * - sepia: the default ochre→umber ramp. Most cells render in this.
 * - vermilion: red-orange. Used for fire, blood, banners of flame —
 *   anything Bosch would have rendered in vermilion lake.
 * - ultramarine: deep blue-violet. Used for sky, water, divine
 *   light, the cool side of chiaroscuro.
 *
 * Animation effects that shift density (shiftDensity, density_pulse,
 * ambient_flicker) automatically shift color along whichever palette
 * a cell is rendering in.
 *
 * The palette is referenced by source FLUX prompt phrasing in
 * illustrator.py: "earthy ochre and umber palette with vermilion
 * and ultramarine accents." Accent palettes are applied per-region
 * via the animation script's `accents` field (see canvas.js / engine.js).
 */
export const SEPIA_BY_CHAR = {
  " ":  null,
  "·":  "#d4b78a",  // pale ochre, just darker than parchment
  ".":  "#c4a06c",
  ":":  "#b08a52",
  ";":  "#9b7540",
  "+":  "#866134",
  "*":  "#714e28",
  "=":  "#5e3e1c",
  "%":  "#4a3014",
  "&":  "#3a240e",
  "@":  "#2a1a08",
  "#":  "#1c1004",  // near-black, deepest umber
};

// Vermilion palette — bumped saturation across the ramp. Sparse end is
// peachier (less ash, more pigment); mid tones go truly red-orange like
// vermilion lake on parchment; dark end is deep oxblood. The previous
// version read as warm brown; this version reads as red.
export const VERMILION_BY_CHAR = {
  " ":  null,
  "·":  "#e8a690",  // pale coral
  ".":  "#dc886c",
  ":":  "#cc6c4c",
  ";":  "#bb5028",
  "+":  "#a83818",
  "*":  "#962508",
  "=":  "#841a04",
  "%":  "#6e1400",
  "&":  "#561000",
  "@":  "#3e0c00",
  "#":  "#280600",  // deep oxblood
};

// Ultramarine palette — bumped saturation for true blue character.
// Sparse end has more cobalt; mid tones go pure ultramarine; dark end
// keeps the violet undertone of the historical pigment. Previous
// version was muted blue-gray; this version reads as ultramarine.
export const ULTRAMARINE_BY_CHAR = {
  " ":  null,
  "·":  "#a8b4d6",  // pale cobalt
  ".":  "#8090c8",
  ":":  "#5e72b8",
  ";":  "#4258a8",
  "+":  "#2c4096",
  "*":  "#1e3284",
  "=":  "#162870",
  "%":  "#0e1e5a",
  "&":  "#081444",
  "@":  "#040e30",
  "#":  "#01081e",  // near-black with deep blue-violet undertone
};

const PALETTES = {
  sepia: SEPIA_BY_CHAR,
  vermilion: VERMILION_BY_CHAR,
  ultramarine: ULTRAMARINE_BY_CHAR,
};

// Backwards-compat alias for any external callers expecting the old name.
export const COLOR_BY_CHAR = SEPIA_BY_CHAR;

/**
 * Frame characters get their own warmer tone — distinguishes the
 * ornamental frame from the painting interior.
 */
const FRAME_COLOR_DOUBLE = "#6b5535";   // ╔═╗║╚╝
const FRAME_COLOR_SINGLE = "#8a6e44";   // ┌─┐│└┘
const FRAME_COLOR_BAND   = "#a88f5c";   // ░ decorative band (light)
const FRAME_COLOR_HEAVY  = "#7d6440";   // ▓ heavy shading band
const FRAME_COLOR_LIGHT  = "#c4a06c";   // ▒ very light band

const FRAME_COLOR_OVERRIDES = {
  "╔": FRAME_COLOR_DOUBLE, "╗": FRAME_COLOR_DOUBLE,
  "╚": FRAME_COLOR_DOUBLE, "╝": FRAME_COLOR_DOUBLE,
  "═": FRAME_COLOR_DOUBLE, "║": FRAME_COLOR_DOUBLE,
  "┌": FRAME_COLOR_SINGLE, "┐": FRAME_COLOR_SINGLE,
  "└": FRAME_COLOR_SINGLE, "┘": FRAME_COLOR_SINGLE,
  "─": FRAME_COLOR_SINGLE, "│": FRAME_COLOR_SINGLE,
  "░": FRAME_COLOR_BAND,
  "▓": FRAME_COLOR_HEAVY,
  "▒": FRAME_COLOR_LIGHT,
};

/**
 * Resolve a character to its display color, or null if the character
 * should render with no color (i.e. as a literal space, no span).
 *
 * @param {string} ch  the cell's character
 * @param {string} [palette]  which palette to look up in. Defaults to
 *   "sepia". Pass "vermilion" or "ultramarine" for accent regions.
 *   Frame characters always render in their warm tone regardless of
 *   palette (the frame is part of the "physical" object, not the
 *   painting interior).
 */
export function colorForChar(ch, palette = "sepia") {
  if (ch === " " || ch === undefined || ch === null) return null;
  if (FRAME_COLOR_OVERRIDES[ch]) return FRAME_COLOR_OVERRIDES[ch];
  const lookup = PALETTES[palette] || SEPIA_BY_CHAR;
  if (ch in lookup) return lookup[ch];
  // Unknown character (e.g. random glyphs from animations) — fall
  // back to a mid-density tone in the chosen palette. Bumped to match
  // the saturated palette ramps above.
  if (palette === "vermilion") return "#841a04";
  if (palette === "ultramarine") return "#162870";
  return "#5e3e1c";
}

/**
 * Map a luminance value (0-255) to a character.
 */
export function charForLuminance(luminance) {
  const idx = Math.min(
    DENSITY_LEVELS - 1,
    Math.max(0, Math.floor((luminance / 255) * DENSITY_LEVELS)),
  );
  return CHARSET[idx];
}

/**
 * Given a current character, return the character one density step
 * brighter (idx-1) or darker (idx+1). Used for flicker / pulse effects.
 * Returns the original char if it's at an extremum.
 */
export function shiftDensity(ch, delta) {
  const idx = CHARSET.indexOf(ch);
  if (idx === -1) return ch;
  const next = Math.min(DENSITY_LEVELS - 1, Math.max(0, idx + delta));
  return CHARSET[next];
}

/**
 * Random character from the charset. Used for noise and decode-reveal.
 */
export function randomChar() {
  return CHARSET[Math.floor(Math.random() * DENSITY_LEVELS)];
}

/**
 * Random character weighted toward sparse end. Looks more like static
 * than uniform random.
 */
export function randomNoiseChar() {
  // Bias toward sparser characters (lower indices)
  const r = Math.random();
  const idx = Math.floor(r * r * DENSITY_LEVELS);
  return CHARSET[idx];
}
